import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueries } from 'react-query';
import api from '../../../lib/api';
import { ELEMENT_TYPE_OPTIONS } from '../../../constants/AssetTestConfig';

/**
 * Hover-preview panel state and per-site asset count queries for the Sites list.
 * Composed by useSites — not imported by page components directly.
 */
export function useSitesHoverPreview(sites, defectCountBySiteId, inProgressBySite) {
  const [hoveredSiteId, setHoveredSiteId] = useState(null);
  const hoverEnterTimeoutRef = useRef(null);
  const hoverLeaveTimeoutRef = useRef(null);

  const hoveredSite = useMemo(() => {
    if (!hoveredSiteId) return null;
    return (sites || []).find((s) => String(s.id) === String(hoveredSiteId)) || null;
  }, [sites, hoveredSiteId]);

  const hoveredDefectMeta = hoveredSite ? defectCountBySiteId.get(String(hoveredSite.id)) : null;
  const hoveredSessions = hoveredSite ? (inProgressBySite.get(Number(hoveredSite.id)) ?? []) : [];

  const hoveredSiteIdStr = hoveredSite?.id != null ? String(hoveredSite.id) : '';
  const hoveredAssetsTotalCountQuery = useQuery(
    ['assetsCount', 'site', hoveredSiteIdStr],
    () => api.get('/assets', { params: { page: 1, limit: 1, siteId: hoveredSiteIdStr } }).then((res) => res.data),
    { staleTime: 60 * 1000, enabled: !!hoveredSiteIdStr }
  );

  const hoveredAssetsByElementTypeQueries = useQueries(
    ELEMENT_TYPE_OPTIONS.map((opt) => ({
      queryKey: ['assetsCount', 'site', hoveredSiteIdStr, 'elementType', opt.value],
      queryFn: () => api.get('/assets', { params: { page: 1, limit: 1, siteId: hoveredSiteIdStr, elementType: opt.value } }).then((res) => res.data),
      staleTime: 60 * 1000,
      enabled: !!hoveredSiteIdStr,
    }))
  );

  const hoveredAssetsSummary = useMemo(() => {
    if (!hoveredSiteIdStr) return { total: 0, breakdown: [], isLoading: false, isError: false };
    const total = Number(hoveredAssetsTotalCountQuery.data?.pagination?.totalCount ?? 0);
    const isLoading = hoveredAssetsTotalCountQuery.isLoading || hoveredAssetsByElementTypeQueries.some((q) => q.isLoading);
    const isError = Boolean(hoveredAssetsTotalCountQuery.error) || hoveredAssetsByElementTypeQueries.some((q) => q.error);
    const breakdown = ELEMENT_TYPE_OPTIONS.map((opt, i) => ({
      key: opt.value,
      label: opt.label,
      count: Number(hoveredAssetsByElementTypeQueries[i]?.data?.pagination?.totalCount ?? 0),
    }));
    return { total, breakdown, isLoading, isError };
  }, [
    hoveredSiteIdStr,
    hoveredAssetsTotalCountQuery.data,
    hoveredAssetsTotalCountQuery.isLoading,
    hoveredAssetsTotalCountQuery.error,
    hoveredAssetsByElementTypeQueries,
  ]);

  useEffect(() => {
    return () => {
      if (hoverEnterTimeoutRef.current) window.clearTimeout(hoverEnterTimeoutRef.current);
      if (hoverLeaveTimeoutRef.current) window.clearTimeout(hoverLeaveTimeoutRef.current);
    };
  }, []);

  return {
    setHoveredSiteId,
    hoverEnterTimeoutRef,
    hoverLeaveTimeoutRef,
    hoveredSite,
    hoveredDefectMeta,
    hoveredSessions,
    hoveredAssetsSummary,
  };
}
